import { useParams, Link as RouterLink } from 'react-router-dom';
import { Box, Container, Typography, Card, CardContent, Button, Grid } from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { psiTokens } from '../theme/psiTheme';
import { getCredential } from '../data/classes';
import CertificateLookupForm from '../components/CertificateLookupForm';
import { usePageMeta } from '../hooks/usePageMeta';

const accentColorFor = (type) => (type === 'certificate' ? psiTokens.credential.certificate : psiTokens.credential.diploma);

// One credential card: a colored banner naming the type, the program name
// in that same color, and the type-specific action (see the linking rule
// in "Diplomas vs Certificates" in CLAUDE.md). When a Record ID has both
// credential types, these render side by side (see the Grid in
// VerifyRecordPage below), not stacked, that was tried first and felt too
// tall/narrow for what's genuinely parallel information.
function CredentialCard({ credential, classId }) {
  const accentColor = accentColorFor(credential.type);
  const isCertificate = credential.type === 'certificate';

  return (
    <Card
      sx={{
        height: '100%',
        backgroundColor: psiTokens.background.primary,
        border: `1px solid ${psiTokens.border}`,
        borderRadius: '14px',
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <Box sx={{ backgroundColor: accentColor, py: 1.5, px: 3, display: 'flex', alignItems: 'center', gap: 1 }}>
        <CheckCircleIcon sx={{ color: '#ffffff', fontSize: 20 }} />
        <Typography sx={{ fontSize: '12px', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: '#ffffff' }}>
          {isCertificate ? 'Certificate' : 'Diploma'}
        </Typography>
      </Box>

      <CardContent sx={{ p: 4, flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
        {/* Just the program name, colored by type — no "Certification"/
            "Diploma" wording repeated, the banner above already said it. */}
        <Typography sx={{ fontSize: '22px', fontWeight: 800, color: accentColor, mb: isCertificate ? 2 : 0, lineHeight: 1.25 }}>
          {credential.programName}
        </Typography>

        {isCertificate ? (
          <Box>
            <Typography sx={{ fontSize: '10px', fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', color: psiTokens.text.tertiary, mb: 0.5 }}>
              Certification Project
            </Typography>
            <Typography sx={{ fontSize: '14px', fontWeight: 600, color: psiTokens.text.secondary, lineHeight: 1.6 }}>
              {credential.certificationProject}
            </Typography>
          </Box>
        ) : (
          <Button
            component={RouterLink}
            to={`/${classId}`}
            variant="outlined"
            fullWidth
            sx={{
              mt: 2,
              borderColor: accentColor,
              color: accentColor,
              fontSize: '12px',
              fontWeight: 700,
              py: 1.2,
              '&:hover': { backgroundColor: 'rgba(138, 125, 153, 0.08)' },
            }}
          >
            View Full Curriculum ({classId})
          </Button>
        )}
      </CardContent>
    </Card>
  );
}

// /verify/:recordId — looks up one Record ID and renders every credential
// it resolves to (a certificate holder also still holds the diploma for
// the same class). See getCredential() in src/data/classes/index.js for
// the rules, and "Diplomas vs Certificates" in CLAUDE.md for the full
// picture.
export default function VerifyRecordPage() {
  const { recordId } = useParams();
  const record = getCredential(recordId);

  // noindex: individual lookup results are per-person utility pages with
  // no names shown (see "Public page privacy" in CLAUDE.md), not content
  // worth ranking in search, /verify (the lookup form) is what belongs
  // there.
  usePageMeta(
    recordId ? `Verify ${recordId}` : 'Verify a Credential',
    record
      ? `Diploma and certificate details for Record ID ${recordId}.`
      : `Look up a PSI diploma or certificate by Record ID to verify a student's training and certification.`,
    { noindex: true }
  );

  return (
    <Box sx={{ backgroundColor: psiTokens.background.tertiary, minHeight: '60vh' }}>
      <Container maxWidth="md" sx={{ py: { xs: 6, md: 10 } }}>
        {/* No small eyebrow above this on purpose, it's the page's only
            heading, not a subtitle under a bigger label. */}
        <Typography variant="h3" sx={{ color: psiTokens.text.primary, fontWeight: 700, mb: 4 }}>
          Verify a student's PSI granted credentials
        </Typography>

        <Box sx={{ maxWidth: '520px' }}>
          <CertificateLookupForm initialValue={recordId} />
        </Box>

        {!record ? (
          <Card
            sx={{
              mt: 4,
              maxWidth: '520px',
              backgroundColor: psiTokens.background.primary,
              border: `1px solid ${psiTokens.border}`,
              borderRadius: '14px',
            }}
          >
            <CardContent sx={{ p: 4, textAlign: 'center' }}>
              <Typography sx={{ fontSize: '16px', fontWeight: 700, color: psiTokens.text.primary, mb: 1 }}>
                No record found
              </Typography>
              <Typography sx={{ fontSize: '13px', color: psiTokens.text.tertiary }}>
                {recordId
                  ? `We couldn't verify a diploma or certificate for Record ID "${recordId}".`
                  : 'Enter a Record ID above to verify a diploma or certificate.'}
              </Typography>
            </CardContent>
          </Card>
        ) : (
          <Box sx={{ mt: 4 }}>
            {/* Shared class info — same regardless of which credential(s)
                apply, so it's shown once instead of once per credential. */}
            <Card
              sx={{
                mb: 3,
                backgroundColor: psiTokens.background.primary,
                border: `1px solid ${psiTokens.border}`,
                borderRadius: '14px',
              }}
            >
              <CardContent sx={{ p: 4 }}>
                <Grid container spacing={2}>
                  <Grid item xs={6} sm={3}>
                    <Typography sx={{ fontSize: '10px', fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', color: psiTokens.text.tertiary, mb: 0.5 }}>
                      Record ID
                    </Typography>
                    <Typography sx={{ fontSize: '14px', fontWeight: 700, color: psiTokens.text.primary, fontFamily: 'monospace' }}>
                      {record.recordId}
                    </Typography>
                  </Grid>
                  <Grid item xs={6} sm={3}>
                    <Typography sx={{ fontSize: '10px', fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', color: psiTokens.text.tertiary, mb: 0.5 }}>
                      Attendance
                    </Typography>
                    <Typography sx={{ fontSize: '14px', fontWeight: 700, color: psiTokens.text.primary }}>
                      {record.pct} ({record.hrs} hrs)
                    </Typography>
                  </Grid>
                  <Grid item xs={6} sm={3}>
                    <Typography sx={{ fontSize: '10px', fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', color: psiTokens.text.tertiary, mb: 0.5 }}>
                      Instructor
                    </Typography>
                    <Typography sx={{ fontSize: '14px', fontWeight: 700, color: psiTokens.text.primary }}>
                      {record.instructor}
                    </Typography>
                  </Grid>
                  <Grid item xs={6} sm={3}>
                    <Typography sx={{ fontSize: '10px', fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', color: psiTokens.text.tertiary, mb: 0.5 }}>
                      Program Dates
                    </Typography>
                    <Typography sx={{ fontSize: '14px', fontWeight: 700, color: psiTokens.text.primary }}>
                      {record.dateRange}
                    </Typography>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>

            {/* Side by side, not stacked — see the comment on
                CredentialCard above for why. A single credential just gets
                the full row instead of being stranded at half width. */}
            <Grid container spacing={3}>
              {record.credentials.map((credential) => (
                <Grid item xs={12} sm={record.credentials.length === 1 ? 12 : 6} key={credential.type}>
                  <CredentialCard credential={credential} classId={record.classId} />
                </Grid>
              ))}
            </Grid>
          </Box>
        )}
      </Container>
    </Box>
  );
}
